'use client';

import Dialog from '@/components/Dialog';
import Image from '@/components/Image';
import Popup from '@/components/Popup';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import { useTranslations } from '@/i18n/use-translations';

export interface GiftListItem {
  id: string | number;
  image: string;
  label?: string;
  name: string;
  isGlass?: boolean;
  color?: string;
  qty?: number;
  validText?: string;
  price?: string;
}

export interface GiftListDialogProps {
  open: boolean;
  title?: string;
  items: GiftListItem[];
  onClose: () => void;
}

export const getGiftListPrice = (price?: string) => (price?.trim() ? price : undefined);

const getGiftListImageClass = (isGlass?: boolean, sizeClass = '') => {
  if (isGlass) {
    return 'h-auto w-4/5 object-contain mix-blend-multiply';
  }

  return `${sizeClass} object-cover`;
};

function PcGiftListContent({
  items,
  giftLabel,
  colorLabel,
  qtyLabel,
}: {
  items: GiftListItem[];
  giftLabel: string;
  colorLabel: string;
  qtyLabel: string;
}) {
  return (
    <div className="relative">
      <div className="max-h-[60vh] overflow-y-auto p-[24px] pt-0">
        {items.map((item) => (
          <div key={item.id} className="mt-[24px] flex h-[128px] justify-between first:mt-0">
            <div className="relative flex h-[128px] w-[128px] items-center justify-center rounded-[5px]">
              <Image
                src={item.image}
                alt={item.name}
                width={128}
                height={128}
                className={`block ${getGiftListImageClass(item.isGlass, 'h-[128px] w-[128px]')}`}
              />
            </div>

            <div className="flex w-[420px] flex-col">
              <span className="mb-[10px] h-[18px] w-fit rounded-[3px] border border-[#ff3052] bg-transparent px-[7px] text-center font-br text-[12px] leading-[18px] text-[#ff3052]">
                {item.label || giftLabel}
              </span>

              <div className="flex items-center justify-between font-bs text-[14px]">
                <span className="max-w-[350px] truncate text-[#282828]">{item.name}</span>
                {getGiftListPrice(item.price) && (
                  <span className="text-[#282828] notranslate">{getGiftListPrice(item.price)}</span>
                )}
              </div>

              {item.color && (
                <span className="mt-[3px] text-[14px] text-[#282828]">
                  {colorLabel}: {item.color}
                </span>
              )}
              {typeof item.qty === 'number' && (
                <span className="mt-[3px] text-[14px] text-[#282828]">
                  {qtyLabel}: {item.qty}
                </span>
              )}
              {item.validText && (
                <div className="mt-auto flex items-center text-[14px] text-[#a9a9a9]">
                  {item.validText}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {items.length > 3 && (
        <div className="absolute bottom-0 left-0 h-[20px] w-full rounded-b-[8px] bg-gradient-to-t from-white to-transparent" />
      )}
    </div>
  );
}

function WapGiftListContent({
  items,
  giftLabel,
  colorLabel,
  qtyLabel,
}: {
  items: GiftListItem[];
  giftLabel: string;
  colorLabel: string;
  qtyLabel: string;
}) {
  return (
    <div className="relative pt-[2px]">
      {items.map((item) => (
        <div key={item.id} className="mt-[16px] flex h-[100px] justify-between first:mt-0">
          <div className="relative flex h-[100px] w-[100px] items-center justify-center overflow-hidden rounded-[8px]">
            <span className="absolute left-0 top-0 z-10 h-[15px] rounded-br-[8px] bg-[#baacff] px-[7px] text-center text-[10px] leading-[15px]">
              {item.label || giftLabel}
            </span>
            <Image
              src={item.image}
              alt={item.name}
              width={100}
              height={100}
              className={`block ${getGiftListImageClass(item.isGlass, 'h-[100px] w-[100px]')}`}
            />
          </div>

          <div className="flex w-[235px] flex-col">
            <div className="flex items-center justify-between font-bs text-[14px]">
              <span className="max-w-[160px] truncate text-[#282828]">{item.name}</span>
              {getGiftListPrice(item.price) && (
                <span className="text-[#282828] notranslate">{getGiftListPrice(item.price)}</span>
              )}
            </div>

            {item.color && (
              <span className="mt-[7px] text-[12px] text-[#282828]">
                {colorLabel}: {item.color}
              </span>
            )}
            {typeof item.qty === 'number' && (
              <span className="mt-[7px] text-[12px] text-[#282828]">
                {qtyLabel}: {item.qty}
              </span>
            )}
            {item.validText && (
              <div className="mt-auto flex items-center text-[12px] text-[#a9a9a9]">
                {item.validText}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function GiftListDialog({ open, title, items, onClose }: GiftListDialogProps) {
  const t = useTranslations('cart.GiftsWithCartItem');

  if (items.length === 0) return null;

  const resolvedTitle = title || t('giftList');
  const labels = {
    gift: t('gift'),
    color: t('color'),
    qty: t('qty'),
  };

  return (
    <>
      <WebOnly>
        <Dialog
          open={open}
          onClose={onClose}
          title={resolvedTitle}
          footer={null}
          sx={{
            '& .MuiDialog-paper': {
              borderRadius: '8px',
            },
            '& .MuiDialogContent-root': {
              padding: '0!important',
            },
          }}
        >
          <div onClick={(event) => event.stopPropagation()}>
            <PcGiftListContent
              items={items}
              giftLabel={labels.gift}
              colorLabel={labels.color}
              qtyLabel={labels.qty}
            />
          </div>
        </Dialog>
      </WebOnly>

      <WapOnly>
        <Popup
          open={open}
          onClose={onClose}
          position="bottom"
          title={resolvedTitle}
          sx={{
            '& .popup-content': {
              borderRadius: '20px 20px 0 0',
              maxHeight: '60vh',
              minHeight: '38vh',
            },
          }}
        >
          <div onClick={(event) => event.stopPropagation()}>
            <WapGiftListContent
              items={items}
              giftLabel={labels.gift}
              colorLabel={labels.color}
              qtyLabel={labels.qty}
            />
          </div>
        </Popup>
      </WapOnly>
    </>
  );
}
